/**
 * SubscriptionWarningBanner
 *
 * A subtle, compact warning strip displayed above the chat composer when
 * the user is approaching a message limit.
 *
 * Four display modes:
 *   1. **Post-trial selling** (5 messages): soft green tint, light CTA
 *   2. **Post-trial lite**   (10 messages): amber tint, slightly more urgent
 *   3. **Paid approaching**  (remaining 1-5): amber strip with reset date
 *   4. **Paid degraded**     (at limit):     soft cedar strip, lighter mode notice
 *
 * Warning levels (from backend enforcement_context):
 *   - null          → No banner shown
 *   - 'approaching' → Amber / green depending on phase
 *   - 'critical'    → Red / amber depending on phase
 *
 * Sprint 7 — Updated Feb 13, 2026: Paid-tier degraded mode + reset date display
 */

import React from 'react';
import { CoachCedarColors } from '../../utils/styleConverter';

// ── Types ──────────────────────────────────────────────────────────────────────

interface SubscriptionWarningBannerProps {
  tier?: string;
  remaining?: number;
  limit?: number;
  warningLevel?: string | null; // 'approaching' | 'critical' | null
  postTrialPhase?: 'selling' | 'lite' | 'blocked' | null;
  degradedMode?: boolean;
  weekEndDate?: string; // ISO date string — when the weekly limit resets
  /** Weekly debriefs used (0-5). Shows approaching-limit warning at 4+. */
  debriefsUsed?: number;
  /** Weekly debrief cap (default 5). */
  debriefsLimit?: number;
  onUpgradeClick?: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Format an ISO date string to a user-friendly weekday + date. */
function formatResetDate(isoDate: string | undefined): string {
  if (!isoDate) return 'next week';
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } catch {
    return 'next week';
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export const SubscriptionWarningBanner: React.FC<SubscriptionWarningBannerProps> = ({
  tier,
  remaining,
  limit,
  warningLevel,
  postTrialPhase,
  degradedMode,
  weekEndDate,
  debriefsUsed,
  debriefsLimit = 5,
  onUpgradeClick,
}) => {
  // Debrief approaching-limit warning (Inconsistency #4 fix)
  // Show when 4 or 5 of 5 debriefs used this week, even if no chat warning
  const debriefsRemaining = debriefsLimit - (debriefsUsed ?? 0);
  const showDebriefWarning = debriefsUsed != null && debriefsUsed >= 4 && debriefsRemaining >= 0;

  // Don't render if no warning needed (chat OR debrief)
  if (!warningLevel && !showDebriefWarning) return null;

  // If only debrief warning (no chat warning), render debrief-specific banner
  if (!warningLevel && showDebriefWarning) {
    const debriefAtLimit = debriefsRemaining <= 0;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 16px',
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          lineHeight: '16px',
          backgroundColor: debriefAtLimit ? '#FEF2F2' : '#FFFBEB',
          borderTop: `1px solid ${debriefAtLimit ? '#FECACA' : '#FDE68A'}`,
          color: debriefAtLimit ? '#991B1B' : '#92400E',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <span>
          {debriefAtLimit ? '\u26A0\uFE0F ' : '\uD83C\uDFCB\uFE0F '}
          {debriefAtLimit
            ? "You've used all 5 debriefs this week \u00B7 Resets next week"
            : `${debriefsRemaining} debrief${debriefsRemaining === 1 ? '' : 's'} left this week`}
        </span>
      </div>
    );
  }

  const isSelling = postTrialPhase === 'selling';
  const isLite = postTrialPhase === 'lite';
  const isPaidTier = tier === 'basic' || tier === 'full' || tier === 'extended';
  const isCritical = warningLevel === 'critical';
  const isAtLimit = remaining !== undefined && remaining <= 0;

  // ── Determine display text per phase ──

  let messageText: string;
  let icon: string;
  let ctaText: string | null;
  const resetLabel = formatResetDate(weekEndDate);

  if (isSelling) {
    // Selling phase: light, not pushy — just make it obvious
    if (isAtLimit) {
      messageText = 'Subscribe to continue chatting with Cedar';
    } else if (remaining !== undefined) {
      messageText = `${remaining} message${remaining === 1 ? '' : 's'} left \u2014 subscribe to continue`;
    } else {
      messageText = 'Subscribe to keep chatting with Cedar';
    }
    icon = '\uD83C\uDF32 '; // 🌲
    ctaText = 'See Plans';
  } else if (isLite) {
    // Lite phase: a touch more direct, still not aggressive
    if (isAtLimit) {
      messageText = 'Your free messages have been used';
    } else if (remaining !== undefined) {
      messageText = `${remaining} free message${remaining === 1 ? '' : 's'} remaining`;
    } else {
      messageText = 'Running low on free messages';
    }
    icon = isCritical ? '\u26A0\uFE0F ' : '\uD83D\uDCAC '; // ⚠️ or 💬
    ctaText = 'Subscribe';
  } else if (isPaidTier) {
    // ── Paid tiers: graceful degradation messaging ──
    if (degradedMode || isAtLimit) {
      // At limit — still allowed to chat in lighter mode
      messageText = `Lighter mode until ${resetLabel}`;
      icon = '\uD83C\uDF32 '; // 🌲 — calm, not alarming
      // Full users can upgrade to Extended for more messages; Extended users have no upgrade path
      ctaText = tier === 'full' ? 'Upgrade' : null;
    } else if (remaining !== undefined) {
      messageText = `${remaining} message${remaining === 1 ? '' : 's'} left this week \u00B7 Resets ${resetLabel}`;
      icon = isCritical ? '\u26A0\uFE0F ' : '\uD83D\uDCAC '; // ⚠️ or 💬
      ctaText = tier === 'full' ? 'Upgrade' : null;
    } else {
      messageText = `Approaching your weekly limit \u00B7 Resets ${resetLabel}`;
      icon = '\uD83D\uDCAC '; // 💬
      ctaText = tier === 'full' ? 'Upgrade' : null;
    }
  } else {
    // Fallback (should not occur for known tiers)
    if (isAtLimit) {
      messageText = "You've reached your weekly message limit";
    } else if (remaining !== undefined) {
      messageText = `${remaining} message${remaining === 1 ? '' : 's'} remaining this week`;
    } else {
      messageText = 'Approaching your weekly message limit';
    }
    icon = isCritical ? '\u26A0\uFE0F ' : '\uD83D\uDCAC ';
    ctaText = 'Upgrade';
  }

  // ── Determine colors per phase ──

  let bgColor: string;
  let borderColor: string;
  let textColor: string;

  if (isSelling) {
    // Soft cedar-green tint — informative, not alarming
    bgColor = isCritical ? '#E8F2ED' : '#F0F7F2';
    borderColor = isCritical ? '#B8D4C0' : '#D4E8DA';
    textColor = CoachCedarColors.cedarPrime;
  } else if (isLite) {
    // Amber / light red — slightly more urgent than selling
    bgColor = isCritical ? '#FEF2F2' : '#FFFBEB';
    borderColor = isCritical ? '#FECACA' : '#FDE68A';
    textColor = isCritical ? '#991B1B' : '#92400E';
  } else if (isPaidTier && (degradedMode || isAtLimit)) {
    // Degraded mode: soft cedar tint — not alarming (they CAN still chat)
    bgColor = '#F0F7F2';
    borderColor = '#D4E8DA';
    textColor = CoachCedarColors.cedarPrime;
  } else {
    // Paid tiers approaching / critical, or fallback
    bgColor = isCritical ? '#FEF2F2' : '#FFFBEB';
    borderColor = isCritical ? '#FECACA' : '#FDE68A';
    textColor = isCritical ? '#991B1B' : '#92400E';
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 16px',
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, sans-serif',
        lineHeight: '16px',
        backgroundColor: bgColor,
        borderTop: `1px solid ${borderColor}`,
        color: textColor,
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <span>
        {icon}
        {messageText}
      </span>

      {ctaText && onUpgradeClick && (
        <button
          onClick={onUpgradeClick}
          style={{
            background: 'none',
            border: 'none',
            color: textColor,
            fontWeight: 600,
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            padding: '2px 8px',
            borderRadius: '4px',
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            whiteSpace: 'nowrap',
            marginLeft: '8px',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.opacity = '0.7'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.opacity = '1'; }}
        >
          {ctaText}
        </button>
      )}
    </div>
  );
};

export default SubscriptionWarningBanner;
